import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import Select from 'react-select';
import toast from 'react-hot-toast';

import Button from '../../../shared/components/ui/Button';
import {
  fetchUnitsWithStages,
  getUnitsWithAdvancePaymentStages,
  clonePaymentStages
} from '../../services/api/implementation/impl';
import CheckboxMultiSelect from '../ui/CheckboxMultiSelect';
import { isAdvanceStageName } from '../../utils/stagePercentCap';

const ClonePaymentStages = ({ projectId, onClose, onSuccess, isSubmitting }) => {
  const [selectedSourceUnit, setSelectedSourceUnit] = useState(null);
  const [selectedTargetUnits, setSelectedTargetUnits] = useState([]);

  // Fetch units with payment stages (for source unit)
  const {
    data: sourceUnits,
    isLoading: isLoadingSource,
    error: sourceError,
    refetch: refetchSourceUnits
  } = useQuery({
    queryKey: ['units_with_stages', projectId],
    queryFn: () => fetchUnitsWithStages(projectId),
    enabled: !!projectId
  });

  // Fetch units with only advance payment (for target units)
  const {
    data: targetUnits,
    isLoading: isLoadingTarget,
    error: targetError,
    refetch: refetchTargetUnits
  } = useQuery({
    queryKey: ['units_with_advance', projectId],
    queryFn: () => getUnitsWithAdvancePaymentStages(projectId),
    enabled: !!projectId
  });

  // React Query v5 dropped onError on useQuery, so surface load failures here.
  useEffect(() => {
    const error = sourceError || targetError;
    if (!error) return;
    toast.error(error?.response?.data?.message || error?.message || 'Failed to load units');
  }, [sourceError, targetError]);

  useEffect(() => {
    if (projectId) {
      refetchSourceUnits();
      refetchTargetUnits();
    }
  }, [projectId]);

  // Transform source units for Select
  // A unit holding only the default Advance stage has nothing worth cloning.
  const sourceUnitOptions =
    sourceUnits
      ?.filter((unit) =>
        (unit.independentPaymentStages || []).some((stage) => !isAdvanceStageName(stage.name))
      )
      .map((unit) => {
        const unitName = unit.unitName || `Unit ${unit.id}`;
        const blockName = unit.block?.name ? ` (${unit.block.name})` : '';
        return {
          value: unit.id,
          label: unitName + blockName
        };
      }) || [];

  // Transform target units for Select
  const targetUnitOptions =
    targetUnits
      ?.filter((unit) => unit.id !== selectedSourceUnit?.value)
      .map((unit) => {
        const unitName = unit.unitName || `Unit ${unit.id}`;
        const blockName = unit.block?.name ? ` (${unit.block.name})` : '';
        return {
          value: unit.id,
          label: unitName + blockName
        };
      }) || [];

  // Clone mutation
  const { mutate: handleClone, isPending: isCloning } = useMutation({
    mutationFn: (payload) => clonePaymentStages(payload),
    onSuccess: (data) => {
      toast.success('Payment stages cloned successfully');
      onSuccess?.();
      onClose();
      // Reset form
      setSelectedSourceUnit(null);
      setSelectedTargetUnits([]);
    },
    onError: (error) => {
      console.error('Error cloning payment stages:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to clone payment stages'
      );
    }
  });

  const handleSubmit = () => {
    if (!selectedSourceUnit) {
      toast.error('Please select a source unit');
      return;
    }

    if (!selectedTargetUnits || selectedTargetUnits.length === 0) {
      toast.error('Please select at least one target unit');
      return;
    }

    const payload = {
      unit_id: selectedSourceUnit.value,
      newUnit_ids: selectedTargetUnits.map((unit) => unit.value)
    };

    handleClone(payload);
  };

  return (
    <div className="mt-6">
      <div className="space-y-6">
        {/* Source Unit (Single Select) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Source Unit (Unit with payment stages to clone)
          </label>
          <Select
            options={sourceUnitOptions}
            value={selectedSourceUnit}
            onChange={(unit) => {
              setSelectedSourceUnit(unit);
              setSelectedTargetUnits((prev) => prev.filter((t) => t.value !== unit?.value));
            }}
            isLoading={isLoadingSource}
            placeholder="Select a unit with payment stages"
            className="basic-single-select"
            classNamePrefix="select"
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 })
            }}
          />
        </div>

        {/* Target Units (Multi Select) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Target Units (Units to clone stages to)
          </label>
          <CheckboxMultiSelect
            options={targetUnitOptions}
            value={selectedTargetUnits}
            onChange={setSelectedTargetUnits}
            isLoading={isLoadingTarget}
            placeholder="Units with only the Advance stage"
            className="basic-multi-select"
            classNamePrefix="select"
          />
        </div>
      </div>

      <div className="flex justify-end gap-4 mt-8">
        <Button displayType="secondary" onClick={onClose} disabled={isCloning || isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={
            isCloning || isSubmitting || !selectedSourceUnit || selectedTargetUnits.length === 0
          }>
          {isCloning ? 'Cloning...' : 'Clone Stages'}
        </Button>
      </div>
    </div>
  );
};

export default ClonePaymentStages;
